import { useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Logo from "@/components/base/Logo";

type FormState = {
  firstname: string;
  lastname: string;
  email: string;
  fin: string;
  idSerial: string;
  idNumber: string;
  phone: string;
  password: string;
  confirmPassword: string;
  terms: boolean;
};

export default function Register() {
  const { t } = useTranslation();
  const [form, setForm] = useState<FormState>({
    firstname: "", lastname: "", email: "", fin: "",
    idSerial: "", idNumber: "", phone: "", password: "",
    confirmPassword: "", terms: false,
  });
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [showPass, setShowPass] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [success, setSuccess] = useState(false);

  const passStrength = (() => {
    const p = form.password;
    if (!p) return 0;
    let score = 0;
    if (p.length >= 8) score++;
    if (/[A-Z]/.test(p)) score++;
    if (/[0-9]/.test(p)) score++;
    if (/[^A-Za-z0-9]/.test(p)) score++;
    return score;
  })();

  const strengthColors = ["bg-red-400", "bg-orange-400", "bg-yellow-400", "bg-green-500"];
  const strengthLabels = ["Zəif", "Orta", "Yaxşı", "Güclü"];

  const validate = (): boolean => {
    const newErrors: Partial<Record<keyof FormState, string>> = {};
    if (form.fin.length !== 7 || !/^\d{7}$/.test(form.fin)) {
      newErrors.fin = t("register.error_fin");
    }
    if (form.password !== form.confirmPassword) {
      newErrors.confirmPassword = t("register.error_password");
    }
    if (!form.terms) {
      newErrors.terms = t("register.error_terms");
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      setSuccess(true);
    }
  };

  const inputClass = (field: keyof FormState) =>
    `w-full border rounded-lg px-3.5 py-2.5 text-sm text-gray-800 outline-none transition-colors ${
      errors[field] ? "border-red-400 focus:border-red-500" : "border-gray-200 focus:border-red-500"
    } bg-white`;

  if (success) {
    return (
      <div className="min-h-screen bg-white flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center px-4 pt-16">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 flex items-center justify-center bg-green-100 rounded-full mx-auto mb-5">
              <i className="ri-checkbox-circle-line text-green-600 text-3xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-3">{t("register.success")}</h2>
            <p className="text-gray-500 text-sm mb-6">{t("register.success_msg")}</p>
            <Link
              to="/login"
              className="inline-block bg-red-600 text-white font-semibold px-7 py-3 rounded-full text-sm cursor-pointer hover:bg-red-700 transition-all"
            >
              {t("nav.login")}
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="pt-16 min-h-screen flex">
        {/* Left Panel */}
        <div className="hidden lg:flex w-2/5 bg-gradient-to-b from-gray-900 to-gray-800 flex-col justify-between px-12 py-14">
          <div>
            <div className="mb-10">
              <Logo variant="light" size="lg" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">{t("register.subtitle")}</h2>
            <p className="text-gray-400 text-sm mb-10">mail.net.az</p>
            <div className="space-y-5">
              {[t("register.feature1"), t("register.feature2"), t("register.feature3")].map((f) => (
                <div key={f} className="flex items-center gap-3">
                  <div className="w-6 h-6 flex items-center justify-center bg-green-500/20 rounded-full flex-shrink-0">
                    <i className="ri-check-line text-green-400 text-xs" />
                  </div>
                  <span className="text-gray-300 text-sm">{f}</span>
                </div>
              ))}
            </div>

            {/* FIN Info Card */}
            <div className="mt-10 bg-white/5 border border-white/10 rounded-xl p-5">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 flex items-center justify-center bg-red-500/20 rounded-lg flex-shrink-0">
                  <i className="ri-id-card-line text-red-400" />
                </div>
                <div>
                  <div className="text-white text-sm font-semibold mb-1">FİN Doğrulaması</div>
                  <div className="text-gray-400 text-xs leading-relaxed">{t("register.fin_info")}</div>
                </div>
              </div>
            </div>
          </div>

          <div className="text-sm text-gray-500">
            {t("register.already")}{" "}
            <Link to="/login" className="text-red-400 hover:text-red-300 cursor-pointer font-medium">
              {t("register.login_link")}
            </Link>
          </div>
        </div>

        {/* Right Panel - Form */}
        <div className="flex-1 overflow-y-auto flex items-start justify-center px-4 md:px-10 py-10">
          <div className="w-full max-w-xl">
            <h1 className="text-2xl font-bold text-gray-900 mb-1">{t("register.title")}</h1>
            <p className="text-gray-400 text-sm mb-7">mail.net.az — {t("register.subtitle")}</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Name Row */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">{t("register.firstname")}</label>
                  <input
                    type="text"
                    name="firstname"
                    required
                    className={inputClass("firstname")}
                    value={form.firstname}
                    onChange={(e) => setForm({ ...form, firstname: e.target.value })}
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-600 mb-1 block">{t("register.lastname")}</label>
                  <input
                    type="text"
                    name="lastname"
                    required
                    className={inputClass("lastname")}
                    value={form.lastname}
                    onChange={(e) => setForm({ ...form, lastname: e.target.value })}
                  />
                </div>
              </div>

              {/* Email */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">{t("register.email")}</label>
                <input
                  type="email"
                  name="email"
                  required
                  placeholder={t("register.email_placeholder")}
                  className={inputClass("email")}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                />
              </div>

              {/* FIN */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1.5">
                  {t("register.fin")}
                  <span className="bg-red-50 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded">TƏLƏBLİ</span>
                </label>
                <input
                  type="text"
                  name="fin"
                  required
                  maxLength={7}
                  placeholder={t("register.fin_placeholder")}
                  className={inputClass("fin")}
                  value={form.fin}
                  onChange={(e) => {
                    const v = e.target.value.replace(/\D/g, "");
                    setForm({ ...form, fin: v });
                  }}
                />
                {errors.fin && <p className="text-red-500 text-xs mt-1">{errors.fin}</p>}
              </div>

              {/* ID Serial + Number */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 flex items-center gap-1.5">
                  Şəxsiyyət Vəsiqəsi
                  <span className="bg-red-50 text-red-600 text-[10px] font-bold px-1.5 py-0.5 rounded">TƏLƏBLİ</span>
                </label>
                <div className="grid grid-cols-3 gap-2">
                  <input
                    type="text"
                    name="idSerial"
                    required
                    maxLength={3}
                    placeholder={t("register.id_serial_placeholder")}
                    className={`${inputClass("idSerial")} uppercase`}
                    value={form.idSerial}
                    onChange={(e) => setForm({ ...form, idSerial: e.target.value.toUpperCase() })}
                  />
                  <input
                    type="text"
                    name="idNumber"
                    required
                    maxLength={8}
                    placeholder={t("register.id_number_placeholder")}
                    className={`${inputClass("idNumber")} col-span-2`}
                    value={form.idNumber}
                    onChange={(e) => {
                      const v = e.target.value.replace(/\D/g, "");
                      setForm({ ...form, idNumber: v });
                    }}
                  />
                </div>
              </div>

              {/* Phone */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">{t("register.phone")}</label>
                <input
                  type="tel"
                  name="phone"
                  required
                  placeholder={t("register.phone_placeholder")}
                  className={inputClass("phone")}
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              {/* Password */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">{t("register.password")}</label>
                <div className="relative">
                  <input
                    type={showPass ? "text" : "password"}
                    name="password"
                    required
                    placeholder={t("register.password_placeholder")}
                    className={`${inputClass("password")} pr-10`}
                    value={form.password}
                    onChange={(e) => setForm({ ...form, password: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPass(!showPass)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                  >
                    <i className={showPass ? "ri-eye-off-line" : "ri-eye-line"} />
                  </button>
                </div>
                {form.password && (
                  <div className="mt-1.5 flex items-center gap-2">
                    <div className="flex gap-1 flex-1">
                      {[...Array(4)].map((_, i) => (
                        <div
                          key={i}
                          className={`h-1 flex-1 rounded-full transition-all ${i < passStrength ? strengthColors[passStrength - 1] : "bg-gray-200"}`}
                        />
                      ))}
                    </div>
                    <span className="text-xs text-gray-500">{passStrength > 0 ? strengthLabels[passStrength - 1] : ""}</span>
                  </div>
                )}
              </div>

              {/* Confirm Password */}
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">{t("register.confirm_password")}</label>
                <div className="relative">
                  <input
                    type={showConfirm ? "text" : "password"}
                    name="confirmPassword"
                    required
                    placeholder={t("register.confirm_password_placeholder")}
                    className={`${inputClass("confirmPassword")} pr-10`}
                    value={form.confirmPassword}
                    onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirm(!showConfirm)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"
                  >
                    <i className={showConfirm ? "ri-eye-off-line" : "ri-eye-line"} />
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-500 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Terms */}
              <div className="flex items-start gap-2.5 pt-1">
                <input
                  type="checkbox"
                  id="terms"
                  className="mt-0.5 accent-red-600 cursor-pointer"
                  checked={form.terms}
                  onChange={(e) => setForm({ ...form, terms: e.target.checked })}
                />
                <label htmlFor="terms" className="text-sm text-gray-600 cursor-pointer">
                  {t("register.terms")}{" "}
                  <a href="#" rel="nofollow" className="text-red-600 underline">
                    {t("register.terms_link")}
                  </a>
                </label>
              </div>
              {errors.terms && <p className="text-red-500 text-xs -mt-2">{errors.terms}</p>}

              <button
                type="submit"
                className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-lg text-sm cursor-pointer transition-all mt-2 whitespace-nowrap"
              >
                {t("register.btn")}
              </button>

              {/* Mobile: already have account */}
              <p className="text-center text-sm text-gray-500 lg:hidden">
                {t("register.already")}{" "}
                <Link to="/login" className="text-red-600 font-medium cursor-pointer">
                  {t("register.login_link")}
                </Link>
              </p>
            </form>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
